import { MessageSquare, SquarePen } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Alert, Spinner } from '@/components/ds2'
import { getApiErrorMessage } from '@/utils/get-user-error-message'
import { listGeneralChats } from '../api/generalAgentChatApi'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import type { GeneralChatSummary } from '../types'

type HistoryState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; chats: GeneralChatSummary[] }

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

const lastActive = (chat: GeneralChatSummary) => DATE_FORMAT.format(new Date(chat.last_activity_at ?? chat.updated_at ?? chat.created_at))

/** The user's past general-agent chats, newest first: open one, or start a new one. */
export const GeneralAgentHistory = () => {
    const currentTabId = useGeneralAgentChatStore((s) => s.tabId)
    const openChat = useGeneralAgentChatStore((s) => s.openChat)
    const reset = useGeneralAgentChatStore((s) => s.reset)
    const [history, setHistory] = useState<HistoryState>({ status: 'loading' })

    useEffect(() => {
        let cancelled = false
        listGeneralChats()
            .then((chats) => !cancelled && setHistory({ status: 'ready', chats }))
            .catch((error: unknown) => !cancelled && setHistory({ status: 'error', message: getApiErrorMessage(error, 'Could not load your chats') }))
        return () => {
            cancelled = true
        }
    }, [])

    return (
        <div className="flex h-full min-h-0 flex-col gap-2">
            <button
                type="button"
                onClick={reset}
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-accent transition-colors duration-150 hover:bg-default"
            >
                <SquarePen className="h-4 w-4" /> New chat
            </button>
            {history.status === 'loading' && (
                <div className="flex flex-1 items-center justify-center">
                    <Spinner size={20} />
                </div>
            )}
            {history.status === 'error' && <Alert variant="danger" title={history.message} />}
            {history.status === 'ready' && history.chats.length === 0 && <p className="m-auto text-sm text-foreground-muted">No chats yet.</p>}
            {history.status === 'ready' && history.chats.length > 0 && (
                <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                    {history.chats.map((chat) => {
                        const current = chat.id === currentTabId
                        return (
                            <li key={chat.id}>
                                <button
                                    type="button"
                                    onClick={() => void openChat(chat.id)}
                                    aria-current={current}
                                    className={`flex w-full items-start gap-2 rounded-xl px-3 py-2 text-left transition-colors duration-150 hover:bg-default ${current ? 'bg-default' : ''}`}
                                >
                                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-foreground-muted" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm text-foreground">{chat.title || 'Untitled chat'}</span>
                                        <span className="block text-xs text-foreground-muted">{lastActive(chat)}</span>
                                    </span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}
